import { p as proxyCustomElement, H, c as createEvent, h, F as Fragment, a as Host } from './index.js';
import { d as defineCustomElement$6 } from './p-BedvZv52.js';
import { d as defineCustomElement$5 } from './p-aAvJ0uEW.js';
import { d as defineCustomElement$4 } from './p-BVCTlSQE.js';
import { d as defineCustomElement$3 } from './p-DaiIyqo3.js';
import { d as defineCustomElement$2 } from './p-Bjt5CD4D.js';
import { d as defineCustomElement$1 } from './p-QtR5VUMp.js';

const getCenterSlice = (pages, currentPage, sliceLength) => {
    if (pages.length <= sliceLength) {
        return pages.slice();
    }
    // Ensure an even number for the sides as we're looking to have a odd center in the slice
    const half = Math.floor(sliceLength / 2);
    // Convert to 0 based
    const idx = currentPage - 1;
    let start = idx - half;
    let end = idx + half;
    // Offset start when necessary
    if (start < 0) {
        end += 0 - start;
        start = 0;
    }
    // Offset end when necessary
    const lastIndex = pages.length - 1;
    if (end > lastIndex) {
        start -= end - lastIndex;
        end = lastIndex;
    }
    // End must be inclusive
    return pages.slice(start, ++end);
};

const tablePageNumberInputCss = ":host{align-items:center;display:flex;font-family:Open Sans;font-size:.875rem;font-weight:400;gap:1rem;letter-spacing:-.0004em;line-height:1.25rem}.boundary-ellipsis{align-items:center;color:var(--jw-theme-surface-text-description-color);display:inline-flex;height:32px;justify-content:center;-webkit-user-select:none;user-select:none;width:32px}.limit-exceeded{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}";

const PAGE_NUMBER_LIMIT = 99;
const PAGE_SLICE_LENGTH = 5;
const WuiTablePageNumberInput = /*@__PURE__*/ proxyCustomElement(class WuiTablePageNumberInput extends H {
    constructor(registerHost) {
        super();
        if (registerHost !== false) {
            this.__registerHost();
        }
        this.__attachShadow();
        this.changePage = createEvent(this, "changePage", 3);
        this.page = 1;
        this.total = 0;
    }
    _watchPage() {
        this.pendingPage = undefined;
    }
    _watchError(state) {
        if (state) {
            // Reset pending page (loading animation) when there's an error
            this.pendingPage = undefined;
        }
    }
    calcPageCount() {
        const { total, pageLength } = this;
        if (total === Number.POSITIVE_INFINITY) {
            return Number.POSITIVE_INFINITY;
        }
        if (typeof pageLength === 'number' && pageLength > 0) {
            // Ensure we always have one page, even when the sum equals 0. We can't have 0 pages. It is illogical.
            return (pageLength && Math.ceil(total / pageLength)) || 1;
        }
        // With no page length limit, we always need to default to 1 page
        return 1;
    }
    goToPage(p) {
        this.pendingPage = p;
        this.changePage.emit(p);
    }
    renderInner(pageCount, disabled) {
        const { page, pendingPage } = this;
        const isPageLimitExceeded = pageCount > PAGE_NUMBER_LIMIT;
        if (isPageLimitExceeded) {
            return (h("span", { class: "limit-exceeded" }, page, " of ", pageCount === Number.POSITIVE_INFINITY ? '∞' : pageCount));
        }
        const pages = Array.from({ length: pageCount }).map((_, k) => k + 1);
        const centerPagesSlice = getCenterSlice(pages, page, PAGE_SLICE_LENGTH);
        const first = centerPagesSlice.at(0);
        const last = centerPagesSlice.at(-1);
        if (typeof first === 'undefined' || typeof last === 'undefined') {
            throw new Error('Undefined: first and/or last');
        }
        const firstPageNav = first > 1;
        const lastPageNav = last < pageCount;
        // We don't need to show the boundaries when we have less pages than our slice length
        const showBoundary = pageCount > centerPagesSlice.length;
        return (h("wui-layout", { type: "button-group", variant: "button-group-tight" }, firstPageNav && showBoundary && (h(Fragment, null, h("wui-button", { type: "ghost", toggled: page === 1, disabled: disabled || page === 1, onClick: () => {
                this.goToPage(1);
            }, loading: pendingPage === 1, label: "1" }), h("span", { class: "boundary-ellipsis" }, "\u2026"))), centerPagesSlice.map((p) => {
            return (h("wui-button", { type: "ghost", toggled: page === p, key: p, label: String(p), onClick: () => {
                    this.goToPage(p);
                }, disabled: disabled || page === p, loading: p === pendingPage }));
        }), lastPageNav && showBoundary && (h(Fragment, null, h("div", { class: "boundary-ellipsis" }, "\u2026"), h("wui-button", { type: "ghost", label: String(pageCount), onClick: () => {
                this.goToPage(pageCount);
            }, toggled: page === pageCount, disabled: disabled || page === pageCount, loading: pendingPage === pageCount })))));
    }
    render() {
        // The `disabled` prop can be a bit delayed by the TableSource so we'll also
        // use internal state to check when we should disable the buttons
        const disabled = this.disabled || (typeof this.pendingPage !== 'undefined' && this.pendingPage !== this.page);
        const pageCount = this.calcPageCount();
        const lessItemsOnPageThanPageLength = typeof this.itemsOnPage === 'number' && typeof this.pageLength === 'number'
            ? this.itemsOnPage < this.pageLength
            : false;
        return (h(Host, { key: 'f7311263f861f47d97e05eb474ec8ee5d7e8ee4a' }, h("wui-button", { key: 'c588fc1974bf5ce7ee6e44706fba4d9d2de72654', label: "Previous", type: "tertiary", disabled: disabled || this.page <= 1, onClick: () => {
                this.goToPage(this.page - 1);
            } }), this.renderInner(pageCount, disabled), h("wui-button", { key: 'e4feb1b31931d886e1da0842381734b9893da400', label: "Next", type: "tertiary", disabled: disabled || this.page >= pageCount || lessItemsOnPageThanPageLength, onClick: () => {
                this.goToPage(this.page + 1);
            } })));
    }
    static get watchers() { return {
        "page": ["_watchPage"],
        "hasError": ["_watchError"]
    }; }
    static get style() { return tablePageNumberInputCss; }
}, [257, "wui-table-page-number-input", {
        "page": [2],
        "pageLength": [2, "page-length"],
        "total": [2],
        "disabled": [516],
        "hasError": [4, "has-error"],
        "itemsOnPage": [2, "items-on-page"],
        "pendingPage": [32]
    }, undefined, {
        "page": ["_watchPage"],
        "hasError": ["_watchError"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-table-page-number-input", "wui-button", "wui-icon", "wui-layout", "wui-loader", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-table-page-number-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiTablePageNumberInput);
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-layout":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiTablePageNumberInput as W, defineCustomElement as d };
